import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn, isValidCPF, checkCPFExists } from '@/lib/utils';
import { format, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  User, 
  Building, 
  Calendar as CalendarIcon, 
  FileText, 
  Shield, 
  AlertTriangle,
  Info,
  Loader2,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { useAddressLookup } from '@/hooks/useAddressLookup';
import { supabase } from '@/integrations/supabase/client';

interface ClientFormData {
  id?: string; // ID opcional para edição
  // Dados Básicos
  name: string;
  client_type: 'person' | 'company';
  cpf_cnpj: string;
  rg_ie: string;
  birth_date?: Date;
  nationality: string;
  marital_status: string;
  profession: string;
  
  // Contato
  email: string;
  phone: string;
  mobile: string;
  alternative_phone: string;
  
  // Endereço
  address: string;
  address_number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  
  // Dados Jurídicos
  how_found_us: string;
  referral_source: string;
  legal_areas: string[];
  client_priority: 'low' | 'medium' | 'high' | 'vip';
  
  // Observações
  notes: string;
  special_requirements: string;
  confidentiality_level: 'normal' | 'high' | 'confidential';
  
  // LGPD
  lgpd_consent: boolean;
  lgpd_consent_date?: Date;
  marketing_consent: boolean;
  data_sharing_consent: boolean;
}

interface ClientFormProps {
  formData: ClientFormData;
  setFormData: (data: ClientFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isEditing: boolean;
}

export const ClientForm = ({ 
  formData, 
  setFormData, 
  onSubmit, 
  onCancel, 
  isEditing 
}: ClientFormProps) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [displayDate, setDisplayDate] = useState<string>(formData.birth_date ? format(formData.birth_date, "dd/MM/yyyy") : "");
  const [cpfValidation, setCpfValidation] = useState<{
    isValid: boolean;
    isChecking: boolean;
    isDuplicate: boolean;
    duplicateClientName?: string;
  }>({
    isValid: true,
    isChecking: false,
    isDuplicate: false
  });

  const { address, cities, loading, error, fetchAddressByCep, fetchCitiesByState, setAddress, setCities } = useAddressLookup();

  useEffect(() => {
    if (address.uf && address.localidade) {
      setFormData({
        ...formData,
        address: address.logradouro || formData.address,
        district: address.bairro || formData.district,
        city: address.localidade || formData.city,
        state: address.uf || formData.state,
        zip_code: address.cep || formData.zip_code,
      });
    }
  }, [address, setFormData, formData]);

  useEffect(() => {
    if (formData.state) {
      fetchCitiesByState(formData.state);
    }
  }, [formData.state, fetchCitiesByState]);

  useEffect(() => {
    setDisplayDate(formData.birth_date ? format(formData.birth_date, "dd/MM/yyyy") : "");
  }, [formData.birth_date]);

  const calculateAge = (birthDate: Date) => {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const age = formData.birth_date ? calculateAge(formData.birth_date) : null;

  // Automatic client priority based on age
  useEffect(() => {
    if (formData.client_type === 'person' && formData.birth_date) {
      const calculatedAge = calculateAge(formData.birth_date);
      let newPriority = formData.client_priority;

      if (calculatedAge <= 14 || calculatedAge >= 60) {
        newPriority = 'high';
      } else if (newPriority === 'high' && !(calculatedAge <= 14 || calculatedAge >= 60)) {
        // If it was high due to age, but now age doesn't qualify, revert to medium
        newPriority = 'medium';
      }

      if (newPriority !== formData.client_priority) {
        setFormData({ ...formData, client_priority: newPriority });
      }
    }
  }, [formData.birth_date, formData.client_type]);

  const handleAreaToggle = (area: string) => {
    const currentAreas = formData.legal_areas || [];
    const newAreas = currentAreas.includes(area)
      ? currentAreas.filter(a => a !== area)
      : [...currentAreas, area];
    
    setFormData({ ...formData, legal_areas: newAreas });
  };

  const formatCPFCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    
    if (formData.client_type === 'person') {
      // CPF format: 000.000.000-00
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      // CNPJ format: 00.000.000/0000-00
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  };

  const handleCPFCNPJChange = async (value: string) => {
    const formatted = formatCPFCNPJ(value);
    setFormData({ ...formData, cpf_cnpj: formatted });

    if (formData.client_type === 'person') {
      const cleanCpf = value.replace(/\D/g, '');
      
      // Validação básica de CPF
      const isValid = isValidCPF(cleanCpf);
      
      setCpfValidation(prev => ({
        ...prev,
        isValid,
        isChecking: false,
        isDuplicate: false,
        duplicateClientName: undefined
      }));

      // Só verifica duplicação se o CPF for válido e tiver 11 dígitos
      if (cleanCpf.length === 11 && isValid) {
        // Adiciona um delay para evitar muitas chamadas durante a digitação
        const timeoutId = setTimeout(async () => {
          setCpfValidation(prev => ({ ...prev, isChecking: true }));
          
          try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              const result = await checkCPFExists(cleanCpf, user.id, isEditing ? formData.id : undefined);
              
              setCpfValidation({
                isValid: true,
                isChecking: false,
                isDuplicate: result.exists,
                duplicateClientName: result.clientName
              });
            }
          } catch (error) {
            console.error('Erro ao verificar CPF:', error);
            setCpfValidation(prev => ({ ...prev, isChecking: false }));
          }
        }, 500); // Delay de 500ms

        // Cleanup do timeout se o usuário continuar digitando
        return () => clearTimeout(timeoutId);
      } else if (cleanCpf.length > 0) {
        setCpfValidation(prev => ({ ...prev, isChecking: false }));
      }
    }
  };

  // Nova função para lidar com a formatação e parsing ao perder o foco
  const handleDateInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/\D/g, ''); // Remove non-digits

    // Tenta formatar para DD/MM/YYYY
    let formatted = '';
    if (cleanedValue.length >= 8) { // Se tiver pelo menos 8 dígitos (DDMMYYYY)
      formatted = `${cleanedValue.substring(0, 2)}/${cleanedValue.substring(2, 4)}/${cleanedValue.substring(4, 8)}`;
    } else if (cleanedValue.length > 4) { // Se tiver mais de 4 dígitos (DDMM)
      formatted = `${cleanedValue.substring(0, 2)}/${cleanedValue.substring(2, 4)}`;
    } else if (cleanedValue.length > 2) { // Se tiver mais de 2 dígitos (DD)
      formatted = `${cleanedValue.substring(0, 2)}`;
    } else {
      formatted = cleanedValue;
    }

    setDisplayDate(formatted); // Atualiza o input com a data formatada

    // Tenta fazer o parse da data formatada
    const parsedDate = parse(formatted, "dd/MM/yyyy", new Date());
    if (parsedDate instanceof Date && !isNaN(parsedDate.getTime())) {
      setFormData({ ...formData, birth_date: parsedDate });
    } else {
      // Se a data for inválida, limpa o birth_date no formData
      setFormData({ ...formData, birth_date: undefined });
    }
  };

  const getCpfValidationMessage = () => {
    if (cpfValidation.isChecking) {
      return (
        <div className="flex items-center gap-2 text-blue-600 text-sm mt-1">
          <Loader2 className="w-4 h-4 animate-spin" />
          Verificando CPF...
        </div>
      );
    }

    if (!cpfValidation.isValid && formData.cpf_cnpj.replace(/\D/g, '').length > 0) {
      return (
        <div className="flex items-center gap-2 text-destructive text-sm mt-1">
          <XCircle className="w-4 h-4" />
          CPF inválido. Verifique os números digitados.
        </div>
      );
    }

    if (cpfValidation.isDuplicate) {
      return (
        <div className="flex items-center gap-2 text-destructive text-sm mt-1">
          <AlertTriangle className="w-4 h-4" />
          CPF já cadastrado para o cliente: {cpfValidation.duplicateClientName}
        </div>
      );
    }

    if (cpfValidation.isValid && formData.cpf_cnpj.replace(/\D/g, '').length === 11) {
      return (
        <div className="flex items-center gap-2 text-green-600 text-sm mt-1">
          <CheckCircle className="w-4 h-4" />
          CPF válido
        </div>
      );
    }

    return null;
  };

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="mb-6 grid h-auto w-full grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1 sm:grid-cols-5">
        <TabsTrigger 
          value="basic" 
          className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm"
        >
          Básico
        </TabsTrigger>
        <TabsTrigger 
          value="contact"
          className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm"
        >
          Contato
        </TabsTrigger>
        <TabsTrigger 
          value="address"
          className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm"
        >
          Endereço
        </TabsTrigger>
        <TabsTrigger 
          value="legal"
          className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm"
        >
          Jurídico
        </TabsTrigger>
        <TabsTrigger 
          value="lgpd"
          className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm"
        >
          LGPD
        </TabsTrigger>
      </TabsList>

      <form onSubmit={onSubmit} className="space-y-6">
        <TabsContent value="basic" className="space-y-6">
          <Card className="bg-white shadow-lg border border-[#c4ab83]/30 hover:shadow-xl transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
              <CardTitle className="flex items-center gap-2 text-xl font-bold text-black drop-shadow-sm">
                <User className="w-5 h-5" />
                Dados Básicos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="client_type">Tipo de Cliente</Label>
                <Select 
                  value={formData.client_type} 
                  onValueChange={(value) => setFormData({ ...formData, client_type: value as 'person' | 'company' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="person">Pessoa Física</SelectItem>
                    <SelectItem value="company">Pessoa Jurídica</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">
                    {formData.client_type === 'person' ? 'Nome Completo' : 'Razão Social'} *
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="cpf_cnpj">
                    {formData.client_type === 'person' ? 'CPF' : 'CNPJ'} *
                  </Label>
                  <Input
                    id="cpf_cnpj"
                    value={formData.cpf_cnpj}
                    onChange={(e) => handleCPFCNPJChange(e.target.value)}
                    placeholder={formData.client_type === 'person' ? '000.000.000-00' : '00.000.000/0000-00'}
                    required
                    className={
                      !cpfValidation.isValid || cpfValidation.isDuplicate 
                        ? "border-destructive" 
                        : cpfValidation.isValid && formData.cpf_cnpj.replace(/\D/g, '').length === 11
                        ? "border-green-500"
                        : ""
                    }
                  />
                  {getCpfValidationMessage()}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="rg_ie">
                    {formData.client_type === 'person' ? 'RG' : 'Inscrição Estadual'}
                  </Label>
                  <Input
                    id="rg_ie"
                    value={formData.rg_ie}
                    onChange={(e) => setFormData({ ...formData, rg_ie: e.target.value })}
                  />
                </div>
                {formData.client_type === 'person' && (
                  <div>
                    <Label>Data de Nascimento</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <div className="relative">
                          <Input
                            id="birth_date_input"
                            value={displayDate}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Atualiza o displayDate com o que o usuário digita
                              setDisplayDate(value);

                              // Tenta sincronizar o formData.birth_date durante a digitação
                              const cleanedValue = value.replace(/\D/g, ''); // Somente números
                              if (cleanedValue.length === 8) {
                                // Formata como DD/MM/YYYY e tenta fazer parse
                                const formatted = `${cleanedValue.substring(0, 2)}/${cleanedValue.substring(2, 4)}/${cleanedValue.substring(4, 8)}`;
                                const parsedDate = parse(formatted, "dd/MM/yyyy", new Date());
                                if (parsedDate instanceof Date && !isNaN(parsedDate.getTime())) {
                                  setFormData({ ...formData, birth_date: parsedDate });
                                }
                              }
                            }}
                            onBlur={handleDateInputBlur} // Adiciona o evento onBlur
                            placeholder="DD/MM/AAAA"
                            className="pr-9"
                          />
                          <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.birth_date}
                          onSelect={(date) => {
                            setFormData({ ...formData, birth_date: date });
                            if (date) {
                              setDisplayDate(format(date, "dd/MM/yyyy"));
                            }
                          }}
                          initialFocus
                          locale={ptBR}
                        />
                      </PopoverContent>
                    </Popover>
                    {age !== null && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Idade: {age} anos
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nationality">Nacionalidade</Label>
                  <Input
                    id="nationality"
                    value={formData.nationality}
                    onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                    placeholder="Ex: Brasileira"
                  />
                </div>
                {formData.client_type === 'person' && (
                  <div>
                    <Label htmlFor="marital_status">Estado Civil</Label>
                    <Select 
                      value={formData.marital_status} 
                      onValueChange={(value) => setFormData({ ...formData, marital_status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="solteiro">Solteiro(a)</SelectItem>
                        <SelectItem value="casado">Casado(a)</SelectItem>
                        <SelectItem value="divorciado">Divorciado(a)</SelectItem>
                        <SelectItem value="viuvo">Viúvo(a)</SelectItem>
                        <SelectItem value="separado">Separado(a)</SelectItem>
                        <SelectItem value="uniao_estavel">União Estável</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {formData.client_type === 'person' && (
                <div>
                  <Label htmlFor="profession">Profissão</Label>
                  <Input
                    id="profession"
                    value={formData.profession}
                    onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                    placeholder="Ex: Advogado, Médico, etc."
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contact" className="space-y-6">
          <Card className="bg-white shadow-lg border border-[#c4ab83]/30 hover:shadow-xl transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
              <CardTitle className="flex items-center gap-2 text-xl font-bold text-black drop-shadow-sm">
                <Building className="w-5 h-5" />
                Dados de Contato
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(00) 0000-0000"
                  />
                </div>
                <div>
                  <Label htmlFor="mobile">Celular</Label>
                  <Input
                    id="mobile"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    placeholder="(00) 00000-0000"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="alternative_phone">Telefone Alternativo</Label>
                <Input
                  id="alternative_phone"
                  value={formData.alternative_phone}
                  onChange={(e) => setFormData({ ...formData, alternative_phone: e.target.value })}
                  placeholder="(00) 0000-0000"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="address" className="space-y-6">
          <Card className="bg-white shadow-lg border border-[#c4ab83]/30 hover:shadow-xl transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
              <CardTitle className="flex items-center gap-2 text-xl font-bold text-black drop-shadow-sm">
                <FileText className="w-5 h-5" />
                Endereço
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="address">Endereço</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Rua, Avenida, etc."
                  />
                </div>
                <div>
                  <Label htmlFor="address_number">Número</Label>
                  <Input
                    id="address_number"
                    value={formData.address_number}
                    onChange={(e) => setFormData({ ...formData, address_number: e.target.value })}
                    placeholder="123"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="complement">Complemento</Label>
                <Input
                  id="complement"
                  value={formData.complement}
                  onChange={(e) => setFormData({ ...formData, complement: e.target.value })}
                  placeholder="Apartamento, sala, etc."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="district">Bairro</Label>
                  <Input
                    id="district"
                    value={formData.district}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    placeholder="Nome do bairro"
                  />
                </div>
                <div>
                  <Label htmlFor="state">Estado</Label>
                  <Select 
                    value={formData.state} 
                    onValueChange={(value) => setFormData({ ...formData, state: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AC">Acre</SelectItem>
                      <SelectItem value="AL">Alagoas</SelectItem>
                      <SelectItem value="AP">Amapá</SelectItem>
                      <SelectItem value="AM">Amazonas</SelectItem>
                      <SelectItem value="BA">Bahia</SelectItem>
                      <SelectItem value="CE">Ceará</SelectItem>
                      <SelectItem value="DF">Distrito Federal</SelectItem>
                      <SelectItem value="ES">Espírito Santo</SelectItem>
                      <SelectItem value="GO">Goiás</SelectItem>
                      <SelectItem value="MA">Maranhão</SelectItem>
                      <SelectItem value="MT">Mato Grosso</SelectItem>
                      <SelectItem value="MS">Mato Grosso do Sul</SelectItem>
                      <SelectItem value="MG">Minas Gerais</SelectItem>
                      <SelectItem value="PA">Pará</SelectItem>
                      <SelectItem value="PB">Paraíba</SelectItem>
                      <SelectItem value="PR">Paraná</SelectItem>
                      <SelectItem value="PE">Pernambuco</SelectItem>
                      <SelectItem value="PI">Piauí</SelectItem>
                      <SelectItem value="RJ">Rio de Janeiro</SelectItem>
                      <SelectItem value="RN">Rio Grande do Norte</SelectItem>
                      <SelectItem value="RS">Rio Grande do Sul</SelectItem>
                      <SelectItem value="RO">Rondônia</SelectItem>
                      <SelectItem value="RR">Roraima</SelectItem>
                      <SelectItem value="SC">Santa Catarina</SelectItem>
                      <SelectItem value="SP">São Paulo</SelectItem>
                      <SelectItem value="SE">Sergipe</SelectItem>
                      <SelectItem value="TO">Tocantins</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">Cidade</Label>
                  <Select 
                    value={formData.city} 
                    onValueChange={(value) => setFormData({ ...formData, city: value })}
                    disabled={cities.length === 0 || loading} // Desabilita se não houver cidades ou estiver carregando
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loading ? "Carregando cidades..." : "Selecione a Cidade"} />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map((city) => (
                        <SelectItem key={city.id} value={city.nome}>
                          {city.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="zip_code">CEP</Label>
                  <Input
                    id="zip_code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    onBlur={(e) => {
                      const cep = e.target.value.replace(/\D/g, '');
                      if (cep.length === 8) {
                        fetchAddressByCep(cep);
                      }
                    }}
                    placeholder="00000-000"
                  />
                  {loading && <Loader2 className="animate-spin h-4 w-4 text-primary mt-1" />}
                  {error && <p className="text-destructive text-sm mt-1">{error}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="country">País</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="Brasil"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="legal" className="space-y-6">
          <Card className="bg-white shadow-lg border border-[#c4ab83]/30 hover:shadow-xl transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
              <CardTitle className="flex items-center gap-2 text-xl font-bold text-black drop-shadow-sm">
                <FileText className="w-5 h-5" />
                Dados Jurídicos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="how_found_us">Como nos conheceu</Label>
                  <Select 
                    value={formData.how_found_us} 
                    onValueChange={(value) => setFormData({ ...formData, how_found_us: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="indicacao">Indicação</SelectItem>
                      <SelectItem value="internet">Internet</SelectItem>
                      <SelectItem value="redes_sociais">Redes Sociais</SelectItem>
                      <SelectItem value="jornal">Jornal</SelectItem>
                      <SelectItem value="radio">Rádio</SelectItem>
                      <SelectItem value="tv">TV</SelectItem>
                      <SelectItem value="outdoor">Outdoor</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="referral_source">Fonte da Indicação</Label>
                  <Input
                    id="referral_source"
                    value={formData.referral_source}
                    onChange={(e) => setFormData({ ...formData, referral_source: e.target.value })}
                    placeholder="Nome de quem indicou"
                  />
                </div>
              </div>

              <div>
                <Label>Áreas de Atuação</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  {[
                    'Direito Civil', 'Direito Penal', 'Direito Trabalhista', 'Direito Previdenciário',
                    'Direito Tributário', 'Direito Administrativo', 'Direito do Consumidor',
                    'Direito de Família', 'Direito Sucessório', 'Direito Imobiliário',
                    'Direito Empresarial', 'Direito Digital'
                  ].map((area) => (
                    <div key={area} className="flex items-center space-x-2">
                      <Checkbox
                        id={area}
                        checked={formData.legal_areas?.includes(area) || false}
                        onCheckedChange={() => handleAreaToggle(area)}
                      />
                      <Label htmlFor={area} className="text-sm">{area}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="client_priority">Prioridade do Cliente</Label>
                <Select 
                  value={formData.client_priority} 
                  onValueChange={(value) => setFormData({ ...formData, client_priority: value as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="vip">VIP</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="notes">Observações</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Observações sobre o cliente..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="special_requirements">Requisitos Especiais</Label>
                <Textarea
                  id="special_requirements"
                  value={formData.special_requirements}
                  onChange={(e) => setFormData({ ...formData, special_requirements: e.target.value })}
                  placeholder="Necessidades especiais, preferências, etc."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="confidentiality_level">Nível de Confidencialidade</Label>
                <Select 
                  value={formData.confidentiality_level} 
                  onValueChange={(value) => setFormData({ ...formData, confidentiality_level: value as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Alto</SelectItem>
                    <SelectItem value="confidential">Confidencial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.lgpd_consent_date && (
                <div className="text-sm text-muted-foreground">
                  Data do consentimento: {format(formData.lgpd_consent_date, "PPP 'às' HH:mm", { locale: ptBR })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lgpd" className="space-y-6">
          <Card className="bg-white shadow-lg border border-[#c4ab83]/30 hover:shadow-xl transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
              <CardTitle className="flex items-center gap-2 text-xl font-bold text-black drop-shadow-sm">
                <Shield className="w-5 h-5" />
                LGPD - Lei Geral de Proteção de Dados
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="lgpd_consent"
                    checked={formData.lgpd_consent}
                    onCheckedChange={(checked) => {
                      setFormData({ 
                        ...formData, 
                        lgpd_consent: checked as boolean,
                        lgpd_consent_date: checked ? new Date() : undefined
                      });
                    }}
                  />
                  <Label htmlFor="lgpd_consent" className="text-sm">
                    Concordo com o tratamento dos meus dados pessoais conforme a LGPD
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="marketing_consent"
                    checked={formData.marketing_consent}
                    onCheckedChange={(checked) => setFormData({ ...formData, marketing_consent: checked as boolean })}
                  />
                  <Label htmlFor="marketing_consent" className="text-sm">
                    Autorizo o recebimento de comunicações de marketing
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="data_sharing_consent"
                    checked={formData.data_sharing_consent}
                    onCheckedChange={(checked) => setFormData({ ...formData, data_sharing_consent: checked as boolean })}
                  />
                  <Label htmlFor="data_sharing_consent" className="text-sm">
                    Autorizo o compartilhamento de dados com parceiros (quando necessário)
                  </Label>
                </div>
              </div>

              <div className="p-4 bg-[#c4ab83]/10 border border-[#c4ab83]/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-[#c4ab83] mt-0.5" />
                  <div className="text-sm text-[#c4ab83]">
                    <p className="font-medium mb-1">Informações sobre a LGPD:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Seus dados serão tratados com segurança e confidencialidade</li>
                      <li>Você pode solicitar a exclusão dos seus dados a qualquer momento</li>
                      <li>Os dados são utilizados apenas para prestação de serviços jurídicos</li>
                      <li>Você pode revogar os consentimentos a qualquer momento</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button 
            type="submit" 
            disabled={
              formData.client_type === 'person' && 
              (!cpfValidation.isValid || cpfValidation.isDuplicate || cpfValidation.isChecking)
            }
          >
            {isEditing ? "Atualizar Cliente" : "Cadastrar Cliente"}
          </Button>
        </div>
      </form>
    </Tabs>
  );
};